/**
 * LevelRow — one level entry in the LevelsSheet.
 *
 * iOS source: LevelRow.tsx.
 *   - 64px badge, connector line between rows (4px, green when completed,
 *     blue leading into current, grey otherwise).
 *   - Title "Level {n}" (current=bold/primary, completed=semibold/primary,
 *     future=semibold/muted).
 *   - Subline: name (muted xxs) + "Goal: {requirement}" (muted xxs).
 *   - ThermometerProgressBar: fill green if completed, blue if current,
 *     empty if future.
 */
import { FC } from "react";
import { colors } from "@/ui";
import ThermometerProgressBar from "@/ui/ThermometerProgressBar";
import LevelBadge from "./LevelBadge";
import type { LevelBadgeState } from "./LevelBadge";

export interface LevelRowProps {
  level: number;
  name: string;
  emoji: string;
  /** Per-level PNG (LEVEL_ICONS parity); falls back to emoji when absent. */
  iconUrl?: string;
  requirement: string;
  state: LevelBadgeState;
  /** Progress within this level, 0..1 (only meaningful for the current row). */
  progress: number;
  /** Whether to draw the connector line above this row. */
  showConnector: boolean;
  /** State of the connector above (drives its color: leads INTO this row). */
  connectorColor: string;
  isLast: boolean;
}

const BADGE = 64;

const LevelRow: FC<LevelRowProps> = ({
  level,
  name,
  emoji,
  iconUrl,
  requirement,
  state,
  progress,
  showConnector,
  connectorColor,
  isLast,
}) => {
  const titleColor = state === "future" ? colors.muted : colors.primary;
  const titleWeight = state === "current" ? 700 : 600;

  const barProgress =
    state === "completed" ? 1 : state === "current" ? progress : 0;

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: 14 }}>
      {/* Badge column + connectors */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          width: BADGE,
        }}
      >
        {showConnector && (
          <div
            style={{
              position: "absolute",
              top: -14,
              left: BADGE / 2 - 2,
              width: 4,
              height: 14,
              background: connectorColor,
            }}
          />
        )}
        <LevelBadge emoji={emoji} iconUrl={iconUrl} state={state} size={BADGE} />
        {!isLast && (
          <div
            style={{
              flex: 1,
              width: 4,
              minHeight: 14,
              background:
                state === "completed" ? colors.green : colors.greyLight,
              marginTop: 0,
            }}
          />
        )}
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          paddingBottom: 16,
        }}
      >
        <span
          style={{
            fontSize: "var(--base-font-size)",
            fontWeight: titleWeight,
            color: titleColor,
          }}
        >
          Level {level}
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: "var(--base-font-size-xs)",
              color: colors.muted,
            }}
          >
            {name}
          </span>
          <span
            style={{
              fontSize: "var(--base-font-size-xs)",
              color: colors.muted,
              whiteSpace: "nowrap",
            }}
          >
            Goal: {requirement}
          </span>
        </div>
        {/* iOS LevelRow.tsx:108-120: completed=green fill, current=blue fill,
            future=transparent fill on a greyLightest track with greyLight
            stripes. */}
        <ThermometerProgressBar
          progress={barProgress}
          height={18}
          fillColor={
            state === "completed"
              ? colors.green
              : state === "current"
                ? colors.blue
                : "transparent"
          }
          trackColor={state === "future" ? colors.greyLightest : undefined}
          stripeColor={state === "future" ? colors.greyLight : undefined}
        />
      </div>
    </div>
  );
};

export default LevelRow;
